import { GQL } from '@aline/graphql';
import { nanoid } from 'nanoid';
import { equals } from 'ramda';
import type { Descendant } from 'slate';
import { Element, Text } from 'slate';

import type { LinkElement, SuggestedEdit } from './internal';

export function linksShouldBeMerged(a: LinkElement, b: LinkElement): boolean {
  return a.href === b.href;
}

export function propsEq(a: Descendant, b: Descendant): boolean {
  if (Element.isElement(a)) {
    if (!Element.isElement(b)) return false;

    const { children: childrenA, suggestedEdits: suggestedEditsA, ...propsA } = a;
    const { children: childrenB, suggestedEdits: suggestedEditsB, ...propsB } = b;

    const filteredEditsA = stripEditTimestamps(suggestedEditsA);
    const filteredEditsB = stripEditTimestamps(suggestedEditsB);

    return equals(
      { ...propsA, suggestedEdits: filteredEditsA },
      { ...propsB, suggestedEdits: filteredEditsB }
    );
  }
  if (!Text.isText(b)) return false;

  const { suggestedEdits: suggestedEditsA, text: textA, ...propsA } = a;
  const { suggestedEdits: suggestedEditsB, text: textB, ...propsB } = b;

  const filteredEditsA = stripEditTimestamps(suggestedEditsA);
  const filteredEditsB = stripEditTimestamps(suggestedEditsB);

  return equals(
    { ...propsA, suggestedEdits: filteredEditsA },
    { ...propsB, suggestedEdits: filteredEditsB }
  );
}

function stripEditTimestamps(edits?: SuggestedEdit[] | null) {
  return edits?.map(({ createdAt, updatedAt, ...edit }) => edit);
}

export function variableVariantToFieldVariant(variant: GQL.MetadataVariant): GQL.FieldVariant {
  switch (variant) {
    case GQL.MetadataVariant.Email:
    case GQL.MetadataVariant.Text:
      return GQL.FieldVariant.Text;
    case GQL.MetadataVariant.Options:
      return GQL.FieldVariant.Dropdown;
    case GQL.MetadataVariant.RadioOptions:
      return GQL.FieldVariant.Radiobutton;
    case GQL.MetadataVariant.Checkbox:
      return GQL.FieldVariant.Checkbox;
    case GQL.MetadataVariant.Date:
      return GQL.FieldVariant.Date;
    case GQL.MetadataVariant.Currency:
    case GQL.MetadataVariant.Decimal:
      return GQL.FieldVariant.Number;
  }
}

export function getIsOptionsVariant(
  variant: unknown
): variant is Extract<
  GQL.MetadataVariant,
  GQL.MetadataVariant.Options | GQL.MetadataVariant.RadioOptions | GQL.MetadataVariant.Checkbox
> {
  return (
    variant === GQL.MetadataVariant.Options ||
    variant === GQL.MetadataVariant.RadioOptions ||
    variant === GQL.MetadataVariant.Checkbox
  );
}

export function generateSuggestedEditGroupId(): string {
  return nanoid(12);
}
